import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PressHero from "@/components/press/PressHero";
import BrandKit from "@/components/press/BrandKit";
import RouteInventory from "@/components/press/RouteInventory";
import RateCard from "@/components/press/RateCard";
import PressContact from "@/components/press/PressContact";
import SponsorCheckoutModal from "@/components/press/SponsorCheckoutModal";
import { openCheckoutWithPromise } from "@/lib/checkout";

const tierToInquiryType = (tier) =>
  tier === "Drop Collab"
    ? "Product Collab"
    : tier === "Series Sponsor"
    ? "Event Sponsorship"
    : "Brand Partnership";

export default function PressPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    inquiry_type: "Brand Partnership",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [inventory, setInventory] = useState([]);

  // Self-serve City Feature checkout
  const [checkoutSlot, setCheckoutSlot] = useState(null);
  const [checkoutForm, setCheckoutForm] = useState({ sponsor_name: "", email: "", website: "", note: "" });
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    api
      .get("/sponsorship/inventory")
      .then((r) => setInventory(r.data || []))
      .catch(() => {});
  }, []);

  const reserveCity = (slot) => {
    // City Feature tier is self-serve via Stripe; bigger tiers go through the contact form
    if (slot.tier === "City Feature") {
      setCheckoutSlot(slot);
      setCheckoutForm({ sponsor_name: "", email: "", website: "", note: "" });
      return;
    }
    setForm((f) => ({
      ...f,
      inquiry_type: tierToInquiryType(slot.tier),
      message:
        f.message ||
        `I'd like to reserve ${slot.city}, ${slot.state} (${slot.tier}, $${slot.price.toLocaleString()}). My brand is well-suited because...`,
    }));
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const selectTier = (tier) => {
    setForm((f) => ({ ...f, inquiry_type: tierToInquiryType(tier) }));
  };

  const startSponsorCheckout = async () => {
    if (!checkoutSlot) return;
    if (!checkoutForm.sponsor_name.trim() || !checkoutForm.email.trim()) {
      toast("Brand name and email are required.");
      return;
    }
    setCheckoutLoading(true);
    try {
      await openCheckoutWithPromise(async () => {
        const { data } = await api.post("/sponsorship/checkout", {
          slot_id: checkoutSlot.id,
          sponsor_name: checkoutForm.sponsor_name.trim(),
          email: checkoutForm.email.trim(),
          website: checkoutForm.website.trim() || null,
          note: checkoutForm.note.trim() || null,
          origin_url: window.location.origin,
        });
        return data.url;
      });
    } catch (e2) {
      toast(formatApiError(e2?.response?.data?.detail) || "Could not start checkout.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/press/contact", form);
      toast("Got it. We'll be in touch.");
      setSent(true);
    } catch (e2) {
      toast(formatApiError(e2?.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="press-page">
      <PressHero />
      <BrandKit />
      <RouteInventory inventory={inventory} onReserve={reserveCity} />
      <RateCard onSelectTier={selectTier} />
      <PressContact
        form={form}
        setForm={setForm}
        onSubmit={submit}
        submitting={submitting}
        sent={sent}
      />

      <footer className="border-t border-white/10 py-8 text-center text-white/40 text-[10px] uppercase tracking-[0.3em] font-mono-tactical">
        © 2026 FMAA · 50 States · No Repeats · All Vibes
      </footer>

      <SponsorCheckoutModal
        slot={checkoutSlot}
        form={checkoutForm}
        setForm={setCheckoutForm}
        onCancel={() => setCheckoutSlot(null)}
        onPay={startSponsorCheckout}
        loading={checkoutLoading}
      />
    </div>
  );
}
